import type { PlatformId, StrategyPlatformCadence } from '../types';
import { assignTimeSlots, type AssignTimeSlotOutput } from './assignTimeSlots';

interface ParsedStrictSlot {
  weekdayIndex: number;
  hour: number;
  minute: number;
  score?: number;
  originalIndex: number;
}

export interface StrategyExactPublicationSlot extends AssignTimeSlotOutput {
  platform: PlatformId;
  slotIndex: number;
}

export interface AssignStrategyPublicationSlotInput {
  platform: PlatformId;
  weekStartDate: string;
  weekEndDate: string;
  cadences?: StrategyPlatformCadence[] | null;
  /** 0-based index within this platform's publications for the week. */
  slotIndex: number;
  /** Stable seed used only when the strategy has no exact cadence slots. */
  fallbackPostId: string;
}

function parseStrictSlot(
  slot: { weekdayIndex?: unknown; timeLocal?: unknown; score?: unknown },
  originalIndex: number,
): ParsedStrictSlot | null {
  const weekdayIndex = Number(slot.weekdayIndex);
  if (!Number.isInteger(weekdayIndex) || weekdayIndex < 0 || weekdayIndex > 6) return null;

  const [rawHour, rawMinute] = String(slot.timeLocal ?? '').split(':').map(Number);
  const hour = Number.isFinite(rawHour) ? Math.max(0, Math.min(23, rawHour)) : 9;
  const minute = Number.isFinite(rawMinute) ? Math.max(0, Math.min(59, rawMinute)) : 0;
  const score = Number(slot.score);
  return {
    weekdayIndex,
    hour,
    minute,
    ...(Number.isFinite(score) ? { score } : {}),
    originalIndex,
  };
}

function buildExactScheduledAt(dateIso: string, slot: ParsedStrictSlot): string {
  const scheduled = new Date(`${dateIso}T00:00:00.000Z`);
  scheduled.setUTCHours(slot.hour, slot.minute, 0, 0);
  return scheduled.toISOString();
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function weekdayIndexOf(date: Date): number {
  return (date.getUTCDay() + 6) % 7;
}

function exactSlotsForPlatform(
  platform: PlatformId,
  cadences?: StrategyPlatformCadence[] | null,
): ParsedStrictSlot[] {
  const cadence = cadences?.find((candidate) => candidate.platform === platform);
  if (!Array.isArray(cadence?.slots)) return [];

  const canonicalByExactTime = new Map<string, ParsedStrictSlot>();
  cadence.slots
    .map((slot, index) => parseStrictSlot(slot, index))
    .filter((slot): slot is ParsedStrictSlot => Boolean(slot))
    .forEach((slot) => {
      const key = `${slot.weekdayIndex}:${slot.hour}:${slot.minute}`;
      const existing = canonicalByExactTime.get(key);
      if (!existing) {
        canonicalByExactTime.set(key, slot);
        return;
      }

      const existingScore = existing.score ?? Number.NEGATIVE_INFINITY;
      const nextScore = slot.score ?? Number.NEGATIVE_INFINITY;
      if (
        nextScore > existingScore
        || (nextScore === existingScore && slot.originalIndex < existing.originalIndex)
      ) {
        canonicalByExactTime.set(key, slot);
      }
    });

  return Array.from(canonicalByExactTime.values()).sort((left, right) => {
    if (left.weekdayIndex !== right.weekdayIndex) return left.weekdayIndex - right.weekdayIndex;
    if (left.hour !== right.hour) return left.hour - right.hour;
    if (left.minute !== right.minute) return left.minute - right.minute;
    return left.originalIndex - right.originalIndex;
  });
}

export function listStrategyPublicationSlotsForWeek(input: {
  platform: PlatformId;
  weekStartDate: string;
  weekEndDate: string;
  cadences?: StrategyPlatformCadence[] | null;
}): StrategyExactPublicationSlot[] {
  const parsedSlots = exactSlotsForPlatform(input.platform, input.cadences);
  if (parsedSlots.length === 0) return [];

  const windowStart = new Date(`${input.weekStartDate}T00:00:00.000Z`);
  const windowEnd = new Date(`${input.weekEndDate}T00:00:00.000Z`);
  if (Number.isNaN(windowStart.getTime()) || Number.isNaN(windowEnd.getTime()) || windowEnd < windowStart) {
    return [];
  }

  const out: StrategyExactPublicationSlot[] = [];
  for (const [slotIndex, slot] of parsedSlots.entries()) {
    for (let cursor = new Date(windowStart); cursor <= windowEnd; cursor = addDays(cursor, 1)) {
      if (weekdayIndexOf(cursor) !== slot.weekdayIndex) continue;
      out.push({
        platform: input.platform,
        slotIndex,
        weekdayIndex: slot.weekdayIndex,
        scheduledAt: buildExactScheduledAt(isoDate(cursor), slot),
        hourBandUsed: [slot.hour, Math.min(24, slot.hour + 1)] as [number, number],
        jitterMinutes: 0,
      });
    }
  }
  return out;
}

export function assignStrategyPublicationSlot(
  input: AssignStrategyPublicationSlotInput,
): AssignTimeSlotOutput {
  const parsedSlots = exactSlotsForPlatform(input.platform, input.cadences);

  if (parsedSlots.length > 0) {
    const eligibleSlots = listStrategyPublicationSlotsForWeek({
      platform: input.platform,
      weekStartDate: input.weekStartDate,
      weekEndDate: input.weekEndDate,
      cadences: input.cadences,
    }).sort((left, right) => left.scheduledAt.localeCompare(right.scheduledAt));
    if (eligibleSlots.length === 0) {
      throw new Error(`No eligible strategy cadence slots for platform "${input.platform}" in ${input.weekStartDate}..${input.weekEndDate}`);
    }
    const slot = eligibleSlots[Math.max(0, Math.floor(input.slotIndex)) % eligibleSlots.length];
    return {
      weekdayIndex: slot.weekdayIndex,
      scheduledAt: slot.scheduledAt,
      hourBandUsed: slot.hourBandUsed,
      jitterMinutes: 0,
    };
  }

  return assignTimeSlots({
    postId: input.fallbackPostId,
    platform: input.platform,
    weekStartDate: input.weekStartDate,
    weekEndDate: input.weekEndDate,
    cadences: input.cadences,
  });
}
